import { useMemo, useState } from "react";

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const RsvpSubmitted = ({
  message,
  uniqueCode,
  showNotificationPause = true,
}) => {
  const tomorrow = useMemo(() => toInputDate(addDays(new Date(), 1)), []);
  const [showPauseForm, setShowPauseForm] = useState(false);
  const [startsAt, setStartsAt] = useState(tomorrow);
  const [endsAt, setEndsAt] = useState(tomorrow);
  const [pauseStatus, setPauseStatus] = useState({ type: "", message: "" });
  const [isSavingPause, setIsSavingPause] = useState(false);

  const maxEndDate = useMemo(() => {
    const startDate = startsAt ? new Date(`${startsAt}T00:00:00`) : new Date();
    return toInputDate(addDays(startDate, 180));
  }, [startsAt]);

  const handlePauseSubmit = async (event) => {
    event.preventDefault();
    setIsSavingPause(true);
    setPauseStatus({ type: "", message: "" });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/event-participations/rsvp/${uniqueCode}/notification-pause`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startsAt, endsAt }),
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Could not save your pause dates.");
      }
      setPauseStatus({
        type: "success",
        message: "Done — future RSVP texts will pause during those dates.",
      });
    } catch (error) {
      setPauseStatus({
        type: "error",
        message: error.message || "Could not save your pause dates.",
      });
    } finally {
      setIsSavingPause(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen p-4"
      style={{
        backgroundImage: "url('/background.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-lg bg-white shadow-lg rounded-lg p-6 border border-gray-200">
        <div className="flex items-center space-x-3">
          <svg
            className="h-8 w-8 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">
            RSVP Confirmed!
          </h2>
        </div>

        <div className="mt-3 text-gray-700 leading-relaxed">
          {typeof message === "string" ? <span>{message}</span> : message}
        </div>
        {uniqueCode && showNotificationPause && (
          <div className="mt-5 border-t border-gray-100 pt-4 text-sm">
            {!showPauseForm ? (
              <button
                type="button"
                onClick={() => setShowPauseForm(true)}
                className="text-[#004a7e] underline"
              >
                Going away soon? Pause future RSVP texts
              </button>
            ) : (
              <form onSubmit={handlePauseSubmit} className="space-y-3">
                <p className="font-semibold text-gray-800">
                  Pause future RSVP texts while you're away.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-gray-700">
                    Start date
                    <input
                      type="date"
                      min={tomorrow}
                      value={startsAt}
                      onChange={(e) => setStartsAt(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 p-2"
                      required
                    />
                  </label>
                  <label className="text-gray-700">
                    End date
                    <input
                      type="date"
                      min={startsAt || tomorrow}
                      max={maxEndDate}
                      value={endsAt}
                      onChange={(e) => setEndsAt(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 p-2"
                      required
                    />
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={isSavingPause}
                  className="rounded bg-[#004a7e] px-4 py-2 font-bold text-white disabled:opacity-50"
                >
                  {isSavingPause ? "Saving..." : "Save pause"}
                </button>
                {pauseStatus.message && (
                  <p
                    className={
                      pauseStatus.type === "success"
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {pauseStatus.message}
                  </p>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RsvpSubmitted;
